import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fmtDate, inr, rows, type ID } from '@hrms/shared';
import { api } from '../../../lib/api';
import { todayISO } from '../../../lib/date';
import { fieldErrors } from '../../../lib/errors';
import { useMut } from '../../../lib/query';
import { useParams } from '../../../navigation/nav';
import { Badge, Button, Card, Columns, DataTable, DateField, EmptyState, PageHeader, Row, Screen, Select, Stack, Text } from '../../../ui';
import { BreakdownTable, type BreakdownWithWarnings } from '../components/Breakdown';
import { AutoGrid, NumField } from '../components/common';
import { EmployeePicker } from '../components/EmployeePicker';

export function SalaryAssignmentScreen() {
  const initial = Number(useParams<{ employee: string }>().employee) || null;
  const [empId, setEmpId] = useState<ID | null>(initial);
  const structs = useQuery({ queryKey: ['payroll', 'structures'], queryFn: () => api.payroll.structures.list() });
  const hist = useQuery({ queryKey: ['employee', empId, 'salary'], queryFn: () => api.employees.salary(empId!), enabled: !!empId });
  const [f, setF] = useState<{ salary_structure_id: ID | null; ctc: number | null; effective_from: string }>({ salary_structure_id: null, ctc: null, effective_from: todayISO() });
  const [preview, setPreview] = useState<BreakdownWithWarnings | null>(null);
  const [errs, setErrs] = useState<Record<string, string>>({});
  const history = [...(hist.data ?? [])].sort((a, b) => b.effective_from.localeCompare(a.effective_from));
  const current = history.find((s) => !s.effective_to);
  const ready = !!f.salary_structure_id && !!f.ctc;
  const pv = useMut(() => api.payroll.structures.preview(f.salary_structure_id!, f.ctc!) as Promise<BreakdownWithWarnings>, { onSuccess: setPreview });
  const assign = useMut(() => api.employees.assignSalary(empId!, { salary_structure_id: f.salary_structure_id!, ctc: f.ctc!, effective_from: f.effective_from }), {
    invalidate: [['employee', empId]],
    success: 'Salary assigned',
    onSuccess: () => {
      setPreview(null);
      setErrs({});
    },
    onError: (e) => setErrs(fieldErrors(e)),
  });
  const hike = current && f.ctc ? ((f.ctc - current.ctc) / current.ctc) * 100 : null;
  const pickEmployee = (v: ID | null) => {
    setEmpId(v);
    setPreview(null);
    setErrs({});
    setF({ salary_structure_id: null, ctc: null, effective_from: todayISO() });
  };

  return (
    <Screen testID="salary-assignment">
      <PageHeader title="Salary assignment" subtitle="Assign or revise an employee's CTC and salary structure." />
      <Card>
        <EmployeePicker label="Employee" value={empId} onChange={pickEmployee} testID="salary-employee" />
      </Card>
      {!empId ? (
        <Card>
          <EmptyState icon="wallet" title="Pick an employee to begin" description="Their salary history and current CTC will appear here." />
        </Card>
      ) : (
        <Columns ratio={[3, 2]}>
          <Stack>
            <Card title={current ? 'Revise salary' : 'Assign salary'} subtitle={current ? `Current CTC ${inr(current.ctc)} since ${fmtDate(current.effective_from)}` : 'No salary assigned yet'}>
              <Stack gap={14}>
                <Select
                  label="Salary structure"
                  required
                  value={f.salary_structure_id}
                  error={errs.salary_structure_id}
                  options={rows(structs.data).map((s) => ({ value: s.id, label: s.name, description: s.description ?? undefined }))}
                  onChange={(v) => {
                    setF({ ...f, salary_structure_id: v });
                    setPreview(null);
                  }}
                  testID="salary-structure"
                />
                <AutoGrid min={200}>
                  <NumField
                    label="Annual CTC (₹)"
                    required
                    allowEmpty
                    value={f.ctc}
                    error={errs.ctc}
                    hint={hike != null && Number.isFinite(hike) ? `${hike >= 0 ? '+' : ''}${hike.toFixed(1)}% vs current` : undefined}
                    onChange={(v) => {
                      setF({ ...f, ctc: v });
                      setPreview(null);
                    }}
                    testID="salary-ctc"
                  />
                  <DateField label="Effective from" required value={f.effective_from} error={errs.effective_from} onChange={(v) => setF({ ...f, effective_from: v })} />
                </AutoGrid>
                <Row justify="flex-end">
                  <Button icon="calc" title="Preview breakup" disabled={!ready} loading={pv.isPending} onPress={() => pv.mutate(undefined)} testID="salary-preview" />
                  <Button variant="primary" title={current ? 'Revise' : 'Assign'} disabled={!ready} loading={assign.isPending} onPress={() => assign.mutate(undefined)} testID="salary-assign" />
                </Row>
              </Stack>
            </Card>
            {preview && (
              <Card title="Breakup preview">
                <BreakdownTable b={preview} />
              </Card>
            )}
          </Stack>
          <Card title="Salary history" flush>
            <DataTable
              rows={history}
              loading={hist.isLoading}
              emptyTitle="No history"
              columns={[
                { key: 'effective_from', title: 'From', width: 110, render: (s) => fmtDate(s.effective_from), sortValue: (s) => s.effective_from },
                { key: 'effective_to', title: 'To', width: 110, render: (s) => (s.effective_to ? fmtDate(s.effective_to) : <Badge tone="success">Current</Badge>), sortValue: (s) => s.effective_to },
                { key: 'structure', title: 'Structure', width: 140, render: (s) => s.salary_structure?.name ?? '—' },
                { key: 'ctc', title: 'CTC', width: 120, align: 'right', render: (s) => inr(s.ctc), sortValue: (s) => s.ctc },
              ]}
            />
            {current?.breakdown && (
              <Text variant="small" style={{ padding: 12 }}>
                {`Current gross / month: ${inr(current.breakdown.gross_monthly)} · take-home ≈ ${inr(current.breakdown.net_monthly_estimate)}`}
              </Text>
            )}
          </Card>
        </Columns>
      )}
    </Screen>
  );
}
